import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  FileText,
  Star,
  Code,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  ThumbsUp,
  ThumbsDown,
  Trash2,
  Eye,
  Link,
  Globe,
  Plus,
  Palette,
  RefreshCw,
  Unplug,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { bookingTypesApi, intakeApi, googleBusinessApi } from "../../services/api";
import api from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import PremiumModal from "../ui/PremiumModal";
import BookingTypeModal from "../calendar/BookingTypeModal";
import QuickCreateIntakeFormModal from "./QuickCreateIntakeFormModal";
import WidgetPreview from "./WidgetPreview";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface BookingType {
  _id: string;
  name: string;
  slug: string;
  duration: number;
  active: boolean;
  description?: string;
}

interface IntakeForm {
  _id: string;
  name: string;
  slug?: string;
  practiceArea?: string;
  active: boolean;
  allowPublicSubmission?: boolean;
  fields?: any[];
}

interface Review {
  _id: string;
  reviewerName: string;
  rating: number;
  text: string;
  status: "pending" | "approved" | "rejected";
  source: "widget" | "manual" | "google" | "import";
  createdAt: string;
}

interface ReviewStats {
  total: number;
  pendingCount: number;
  averageRating: number;
}

interface GoogleBusinessStatus {
  connected: boolean;
  placeName?: string;
  googleAccountEmail?: string;
  lastSyncAt?: string;
  syncEnabled?: boolean;
}

// ─── Animation Variants ─────────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, stiffness: 300, damping: 30 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 },
};

// ─── Embed Code Display Component ───────────────────────────────────────────────

interface EmbedCodeDisplayProps {
  code: string;
  directLink?: string;
  instructions?: string;
}

const EmbedCodeDisplay: React.FC<EmbedCodeDisplayProps> = ({
  code,
  directLink,
  instructions,
}) => {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopy = async (text: string, type: "code" | "link") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "code") {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      if (type === "code") {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    }
  };

  // Simple syntax coloring for embed code
  const renderColoredCode = (raw: string) => {
    return raw.split("\n").map((line, i) => {
      // Comment lines
      if (line.trim().startsWith("<!--")) {
        return (
          <div key={i} className="text-gray-500">
            {line}
          </div>
        );
      }
      // HTML tags with attributes
      const colored = line
        .replace(
          /(&lt;|<)(\/?\w+)/g,
          '<span class="text-red-400">$1$2</span>'
        )
        .replace(
          /(data-[\w-]+|src|async)/g,
          '<span class="text-yellow-300">$1</span>'
        )
        .replace(
          /="([^"]*)"/g,
          '=<span class="text-emerald-400">"$1"</span>'
        )
        .replace(
          /(&gt;|>)/g,
          '<span class="text-red-400">$1</span>'
        );
      return (
        <div
          key={i}
          dangerouslySetInnerHTML={{ __html: colored || "&nbsp;" }}
        />
      );
    });
  };

  return (
    <div className="space-y-4">
      {instructions && (
        <p className="text-sm text-slate-600">{instructions}</p>
      )}

      {/* Code block */}
      <div className="relative rounded-xl overflow-hidden border border-slate-700">
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Code className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              HTML Embed Code
            </span>
          </div>
          <button
            onClick={() => handleCopy(code, "code")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
            style={{
              backgroundColor: copied ? "#198754" : "rgba(255,255,255,0.08)",
              color: copied ? "#fff" : "#94a3b8",
            }}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy to Clipboard
              </>
            )}
          </button>
        </div>
        <div className="bg-slate-900 p-4 overflow-x-auto">
          <pre className="text-sm font-mono leading-relaxed text-slate-300">
            <code>{renderColoredCode(code)}</code>
          </pre>
        </div>
      </div>

      {/* Direct link */}
      {directLink && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <Link className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="text-sm text-slate-600 truncate flex-1 font-mono">
            {directLink}
          </span>
          <button
            onClick={() => handleCopy(directLink, "link")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex-shrink-0"
            style={{
              backgroundColor: linkCopied
                ? "#198754"
                : "var(--color-primary, #0F5132)",
              color: "#fff",
            }}
          >
            {linkCopied ? (
              <>
                <Check className="w-3 h-3" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                Copy Link
              </>
            )}
          </button>
          <a
            href={directLink}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0"
          >
            <ExternalLink className="w-4 h-4 text-slate-500" />
          </a>
        </div>
      )}
    </div>
  );
};

// ─── Star Rating Display ────────────────────────────────────────────────────────

const StarRating: React.FC<{ rating: number; size?: number }> = ({
  rating,
  size = 16,
}) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={
          star <= rating
            ? "fill-amber-400 text-amber-400"
            : "text-slate-300"
        }
        style={{ width: size, height: size }}
      />
    ))}
  </div>
);

// ─── Status Badge ───────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{
  status: "pending" | "approved" | "rejected";
}> = ({ status }) => {
  const styles: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// ─── Empty State ────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{
  icon: React.FC<any>;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ icon: Icon, title, message, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
      <Icon className="w-6 h-6 text-slate-400" />
    </div>
    <p className="text-sm font-medium text-slate-700">{title}</p>
    <p className="text-xs text-slate-500 mt-1 max-w-sm">{message}</p>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
        style={{ backgroundColor: "var(--color-primary, #0F5132)" }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover, #146C43)")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary, #0F5132)")}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// ─── Loading State ──────────────────────────────────────────────────────────────

const LoadingState: React.FC = () => (
  <div className="flex items-center justify-center py-10">
    <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
    <span className="ml-2 text-sm text-slate-500">Loading...</span>
  </div>
);

// ─── Error State ────────────────────────────────────────────────────────────────

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-3">
      <AlertCircle className="w-6 h-6 text-red-500" />
    </div>
    <p className="text-sm font-medium text-slate-700">
      Something went wrong
    </p>
    <p className="text-xs text-slate-500 mt-1">{message}</p>
    <button
      onClick={onRetry}
      className="mt-3 px-4 py-1.5 text-xs font-medium rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
    >
      Try Again
    </button>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// WidgetsPage
// ═══════════════════════════════════════════════════════════════════════════════

const WidgetsPage: React.FC = () => {
  const navigate = useNavigate();
  const { firmData } = useUserDataContext();
  const firmSlug = firmData?.slug || "";
  const baseUrl = window.location.origin;

  // ── Booking Types State ──────────────────────────────────────────────────────
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [bookingLoading, setBookingLoading] = useState(true);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingExpanded, setBookingExpanded] = useState(true);
  const [embedModalBooking, setEmbedModalBooking] =
    useState<BookingType | null>(null);

  // ── Intake Forms State ───────────────────────────────────────────────────────
  const [intakeForms, setIntakeForms] = useState<IntakeForm[]>([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formsError, setFormsError] = useState<string | null>(null);
  const [formsExpanded, setFormsExpanded] = useState(true);
  const [embedModalForm, setEmbedModalForm] = useState<IntakeForm | null>(
    null
  );
  const [togglingFormId, setTogglingFormId] = useState<string | null>(null);

  // ── Reviews State ────────────────────────────────────────────────────────────
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewStats, setReviewStats] = useState<ReviewStats>({
    total: 0,
    pendingCount: 0,
    averageRating: 0,
  });
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [reviewsExpanded, setReviewsExpanded] = useState(true);
  const [showReviewsTable, setShowReviewsTable] = useState(false);
  const [showReviewsEmbed, setShowReviewsEmbed] = useState(false);
  const [actioningReviewId, setActioningReviewId] = useState<string | null>(
    null
  );

  // ── Create Modal State ───────────────────────────────────────────────────
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // ── Inline Preview State ────────────────────────────────────────────────
  const [previewBooking, setPreviewBooking] = useState<string | null>(null);
  const [previewForm, setPreviewForm] = useState<string | null>(null);
  const [showReviewsPreview, setShowReviewsPreview] = useState(false);

  // ── Color Customization State ──────────────────────────────────────────────
  const defaultColor = firmData?.branding?.primaryColor || "#0F5132";
  const [embedColor, setEmbedColor] = useState(defaultColor.replace("#", ""));

  // ── Google Business State ──────────────────────────────────────────────────
  const [gbStatus, setGbStatus] = useState<GoogleBusinessStatus>({ connected: false });
  const [gbLoading, setGbLoading] = useState(true);
  const [gbSyncing, setGbSyncing] = useState(false);
  const [gbConnecting, setGbConnecting] = useState(false);

  // ── Fetch Data ───────────────────────────────────────────────────────────────

  const fetchBookingTypes = useCallback(async () => {
    setBookingLoading(true);
    setBookingError(null);
    try {
      const res = await bookingTypesApi.list({ active: true });
      const items = res.data?.data || [];
      setBookingTypes(items);
    } catch (err: any) {
      console.error("Failed to fetch booking types:", err);
      setBookingError(
        err.response?.data?.message || "Failed to load booking types"
      );
    } finally {
      setBookingLoading(false);
    }
  }, []);

  const fetchIntakeForms = useCallback(async () => {
    setFormsLoading(true);
    setFormsError(null);
    try {
      const res = await intakeApi.listForms();
      const items = res.data?.data || res.data || [];
      setIntakeForms(Array.isArray(items) ? items : []);
    } catch (err: any) {
      console.error("Failed to fetch intake forms:", err);
      setFormsError(
        err.response?.data?.message || "Failed to load intake forms"
      );
    } finally {
      setFormsLoading(false);
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      // Fetch all reviews for the table
      const res = await api.get("/api/reviews", {
        params: { limit: 100, sort: "-createdAt" },
      });
      const items = res.data?.data || [];
      setReviews(items);

      // Compute stats from the full list
      const total = res.data?.pagination?.total || items.length;
      const pendingCount = items.filter(
        (r: Review) => r.status === "pending"
      ).length;
      const approved = items.filter(
        (r: Review) => r.status === "approved"
      );
      const averageRating =
        approved.length > 0
          ? approved.reduce((sum: number, r: Review) => sum + r.rating, 0) /
            approved.length
          : 0;

      setReviewStats({ total, pendingCount, averageRating });
    } catch (err: any) {
      console.error("Failed to fetch reviews:", err);
      setReviewsError(
        err.response?.data?.message || "Failed to load reviews"
      );
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  const fetchGoogleBusinessStatus = useCallback(async () => {
    setGbLoading(true);
    try {
      const res = await googleBusinessApi.status();
      setGbStatus(res.data || { connected: false });
    } catch {
      setGbStatus({ connected: false });
    } finally {
      setGbLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookingTypes();
    fetchIntakeForms();
    fetchReviews();
    fetchGoogleBusinessStatus();
  }, [fetchBookingTypes, fetchIntakeForms, fetchReviews, fetchGoogleBusinessStatus]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleTogglePublicSubmission = async (form: IntakeForm) => {
    setTogglingFormId(form._id);
    try {
      await intakeApi.updateForm(form._id, {
        allowPublicSubmission: !form.allowPublicSubmission,
      });
      setIntakeForms((prev) =>
        prev.map((f) =>
          f._id === form._id
            ? { ...f, allowPublicSubmission: !f.allowPublicSubmission }
            : f
        )
      );
    } catch (err) {
      console.error("Failed to toggle public submission:", err);
    } finally {
      setTogglingFormId(null);
    }
  };

  const handleReviewAction = async (
    reviewId: string,
    action: "approve" | "reject" | "delete"
  ) => {
    setActioningReviewId(reviewId);
    try {
      if (action === "delete") {
        await api.delete(`/api/reviews/${reviewId}`);
        setReviews((prev) => prev.filter((r) => r._id !== reviewId));
      } else {
        await api.put(`/api/reviews/${reviewId}/${action}`);
        setReviews((prev) =>
          prev.map((r) =>
            r._id === reviewId
              ? { ...r, status: action === "approve" ? "approved" : "rejected" }
              : r
          )
        );
      }
      // Recompute stats
      setReviews((prev) => {
        const total = prev.length;
        const pendingCount = prev.filter(
          (r) => r.status === "pending"
        ).length;
        const approved = prev.filter((r) => r.status === "approved");
        const averageRating =
          approved.length > 0
            ? approved.reduce((sum, r) => sum + r.rating, 0) /
              approved.length
            : 0;
        setReviewStats({ total, pendingCount, averageRating });
        return prev;
      });
    } catch (err) {
      console.error(`Failed to ${action} review:`, err);
    } finally {
      setActioningReviewId(null);
    }
  };

  // ── Google Business Handlers ──────────────────────────────────────────────────

  const handleConnectGoogle = async () => {
    setGbConnecting(true);
    try {
      const res = await googleBusinessApi.getAuthUrl();
      if (res.data?.authUrl) {
        window.location.href = res.data.authUrl;
      }
    } catch (err: any) {
      console.error("Failed to get Google auth URL:", err);
      setGbConnecting(false);
    }
  };

  const handleSyncGoogle = async () => {
    setGbSyncing(true);
    try {
      await googleBusinessApi.sync();
      await fetchReviews();
      await fetchGoogleBusinessStatus();
    } catch (err) {
      console.error("Google review sync failed:", err);
    } finally {
      setGbSyncing(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await googleBusinessApi.disconnect();
      setGbStatus({ connected: false });
    } catch (err) {
      console.error("Failed to disconnect Google Business:", err);
    }
  };

  // ── Embed Code Generators ────────────────────────────────────────────────────

  const colorAttr = embedColor ? ` data-color="${embedColor}"` : "";

  const getBookingEmbedCode = (bt: BookingType) =>
    `<!-- Kavoa Booking Widget -->\n<script src="${baseUrl}/widget/kavoa-embed.js" async></script>\n<div data-kavoa="booking" data-firm="${firmSlug}" data-slug="${bt.slug}"${colorAttr}></div>`;

  const getBookingDirectLink = (bt: BookingType) =>
    `${baseUrl}/book/${firmSlug}/${bt.slug}`;

  const getFormEmbedCode = (form: IntakeForm) =>
    `<!-- Kavoa Form Widget -->\n<script src="${baseUrl}/widget/kavoa-embed.js" async></script>\n<div data-kavoa="form" data-firm="${firmSlug}" data-slug="${form.slug || form._id}"${colorAttr}></div>`;

  const getReviewsEmbedCode = () =>
    `<!-- Kavoa Reviews Widget -->\n<script src="${baseUrl}/widget/kavoa-embed.js" async></script>\n<div data-kavoa="reviews" data-firm="${firmSlug}"${colorAttr}></div>`;

  // ── Section Header ───────────────────────────────────────────────────────────

  const SectionHeader: React.FC<{
    icon: React.FC<any>;
    title: string;
    description: string;
    expanded: boolean;
    onToggle: () => void;
    badgeCount?: number;
  }> = ({ icon: Icon, title, description, expanded, onToggle, badgeCount }) => (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-4 text-left group"
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: "var(--color-primary, #0F5132)" }}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {typeof badgeCount === "number" && badgeCount > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
              style={{
                backgroundColor: "var(--color-accent, #198754)",
              }}
            >
              {badgeCount}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-0.5">{description}</p>
      </div>
      <div className="p-2 rounded-lg group-hover:bg-slate-100 transition-colors">
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </div>
    </button>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-4"
        >
          <ChevronDown className="w-4 h-4 rotate-90" />
          Back to Settings
        </button>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "var(--color-primary, #0F5132)" }}
            >
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              Website Widgets
            </h1>
          </div>
          <p className="text-slate-500 mt-1 ml-[52px]">
            Embed beautiful widgets on your website to accept bookings, collect
            leads, and showcase reviews. Copy the embed code and paste it into your website's HTML.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* ═══════════════════════════════════════════════════════════════════
              Section 1: Booking Widget
              ═══════════════════════════════════════════════════════════════════ */}
          <motion.div
            variants={cardVariants}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <SectionHeader
                    icon={Calendar}
                    title="Booking Widget"
                    description="Let clients book appointments directly from your website"
                    expanded={bookingExpanded}
                    onToggle={() => setBookingExpanded(!bookingExpanded)}
                    badgeCount={bookingTypes.length}
                  />
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowCreateBooking(true); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition-colors flex-shrink-0"
                  style={{ backgroundColor: "var(--color-primary, #0F5132)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover, #146C43)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary, #0F5132)")}
                  title="Create a new booking type"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New
                </button>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {bookingExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 border-t border-slate-100 pt-4">
                    {bookingLoading ? (
                      <LoadingState />
                    ) : bookingError ? (
                      <ErrorState
                        message={bookingError}
                        onRetry={fetchBookingTypes}
                      />
                    ) : bookingTypes.length === 0 ? (
                      <EmptyState
                        icon={Calendar}
                        title="No booking types yet"
                        message="Create a booking type to generate an embed code for your website."
                        actionLabel="Create a Booking Type"
                        onAction={() => setShowCreateBooking(true)}
                      />
                    ) : (
                      <div className="space-y-3">
                        {bookingTypes.map((bt, index) => (
                          <div key={bt._id}>
                            <motion.div
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                              transition={{ delay: index * 0.05 }}
                              className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors group"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                  <Calendar className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-900 truncate">
                                    {bt.name}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <Clock className="w-3 h-3 text-slate-400" />
                                    <span className="text-xs text-slate-500">
                                      {bt.duration} min
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setPreviewBooking(previewBooking === bt._id ? null : bt._id)}
                                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                                    previewBooking === bt._id
                                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                                  }`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  Preview
                                </button>
                                <a
                                  href={getBookingDirectLink(bt)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 rounded-lg hover:bg-slate-100 transition-colors opacity-0 group-hover:opacity-100"
                                  title="Open booking page"
                                >
                                  <ExternalLink className="w-4 h-4 text-slate-400" />
                                </a>
                                <button
                                  onClick={() => setEmbedModalBooking(bt)}
                                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-white transition-colors"
                                  style={{
                                    backgroundColor:
                                      "var(--color-primary, #0F5132)",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "var(--color-primary-hover, #146C43)")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                      "var(--color-primary, #0F5132)")
                                  }
                                >
                                  <Code className="w-3.5 h-3.5" />
                                  Get Embed Code
                                </button>
                              </div>
                            </motion.div>
                            {/* Inline preview */}
                            <AnimatePresence>
                              {previewBooking === bt._id && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden mt-2"
                                >
                                  <WidgetPreview
                                    type="booking"
                                    slug={bt.slug}
                                    color={embedColor}
                                    firmSlug={firmSlug}
                                  />
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ═══════════════════════════════════════════════════════════════════
              Section 2: Contact Form Widget
              ═══════════════════════════════════════════════════════════════════ */}
          <motion.div
            variants={cardVariants}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <SectionHeader
                    icon={FileText}
                    title="Contact Form Widget"
                    description="Embed your intake forms to capture leads from your website"
                    expanded={formsExpanded}
                    onToggle={() => setFormsExpanded(!formsExpanded)}
                    badgeCount={intakeForms.filter((f) => f.active).length}
                  />
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowCreateForm(true); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition-colors flex-shrink-0"
                  style={{ backgroundColor: "var(--color-primary, #0F5132)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover, #146C43)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary, #0F5132)")}
                  title="Create a new intake form"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New
                </button>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {formsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 border-t border-slate-100 pt-4">
                    {formsLoading ? (
                      <LoadingState />
                    ) : formsError ? (
                      <ErrorState
                        message={formsError}
                        onRetry={fetchIntakeForms}
                      />
                    ) : intakeForms.length === 0 ? (
                      <EmptyState
                        icon={FileText}
                        title="No contact forms yet"
                        message="Create a contact form to start capturing leads from your website."
                        actionLabel="Create a Contact Form"
                        onAction={() => setShowCreateForm(true)}
                      />
                    ) : (
                      <div className="space-y-3">
                        {intakeForms.map((form, index) => (
                          <div key={form._id}>
                            <motion.div
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                              transition={{ delay: index * 0.05 }}
                              className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-4 h-4 text-blue-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-slate-900 truncate">
                                      {form.name}
                                    </p>
                                    {!form.active && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                  {form.practiceArea && form.practiceArea !== "other" && (
                                    <p className="text-xs text-slate-500 mt-0.5 capitalize">
                                      {form.practiceArea.replace(/_/g, " ")}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-3 flex-shrink-0">
                                {/* Public submission toggle */}
                                <button
                                  onClick={() =>
                                    handleTogglePublicSubmission(form)
                                  }
                                  disabled={togglingFormId === form._id}
                                  className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50"
                                  title={
                                    form.allowPublicSubmission
                                      ? "Disable public submission"
                                      : "Enable public submission"
                                  }
                                >
                                  {togglingFormId === form._id ? (
                                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                  ) : form.allowPublicSubmission ? (
                                    <ToggleRight
                                      className="w-6 h-6"
                                      style={{
                                        color:
                                          "var(--color-accent, #198754)",
                                      }}
                                    />
                                  ) : (
                                    <ToggleLeft className="w-6 h-6 text-slate-300" />
                                  )}
                                  <span className="hidden sm:inline whitespace-nowrap">
                                    {form.allowPublicSubmission
                                      ? "Public"
                                      : "Private"}
                                  </span>
                                </button>

                                {/* Preview button */}
                                {form.allowPublicSubmission && (
                                  <button
                                    onClick={() => setPreviewForm(previewForm === form._id ? null : form._id)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                                      previewForm === form._id
                                        ? "border-blue-300 bg-blue-50 text-blue-700"
                                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                                    }`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    Preview
                                  </button>
                                )}

                                {/* Get embed code - only when public */}
                                {form.allowPublicSubmission && (
                                  <button
                                    onClick={() => setEmbedModalForm(form)}
                                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-white transition-colors"
                                    style={{
                                      backgroundColor:
                                        "var(--color-primary, #0F5132)",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.backgroundColor =
                                        "var(--color-primary-hover, #146C43)")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.backgroundColor =
                                        "var(--color-primary, #0F5132)")
                                    }
                                  >
                                    <Code className="w-3.5 h-3.5" />
                                    Get Embed Code
                                  </button>
                                )}
                              </div>
                            </motion.div>
                            {/* Inline preview */}
                            <AnimatePresence>
                              {previewForm === form._id && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden mt-2"
                                >
                                  <WidgetPreview
                                    type="form"
                                    slug={form.slug || form._id}
                                    color={embedColor}
                                    firmSlug={firmSlug}
                                  />
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ═══════════════════════════════════════════════════════════════════
              Section 3: Reviews Widget
              ═══════════════════════════════════════════════════════════════════ */}
          <motion.div
            variants={cardVariants}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
          >
            <div className="p-6">
              <SectionHeader
                icon={Star}
                title="Reviews Widget"
                description="Showcase client reviews and testimonials on your website"
                expanded={reviewsExpanded}
                onToggle={() => setReviewsExpanded(!reviewsExpanded)}
                badgeCount={reviewStats.total}
              />
            </div>

            <AnimatePresence initial={false}>
              {reviewsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 border-t border-slate-100 pt-4">
                    {reviewsLoading ? (
                      <LoadingState />
                    ) : reviewsError ? (
                      <ErrorState
                        message={reviewsError}
                        onRetry={fetchReviews}
                      />
                    ) : (
                      <div className="space-y-5">
                        {/* Stats Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                              Total Reviews
                            </p>
                            <p className="text-2xl font-bold text-slate-900 mt-1">
                              {reviewStats.total}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                            <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">
                              Pending Approval
                            </p>
                            <p className="text-2xl font-bold text-amber-700 mt-1">
                              {reviewStats.pendingCount}
                            </p>
                          </div>
                          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">
                              Average Rating
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-2xl font-bold text-emerald-700">
                                {reviewStats.averageRating > 0
                                  ? reviewStats.averageRating.toFixed(1)
                                  : "--"}
                              </p>
                              {reviewStats.averageRating > 0 && (
                                <StarRating
                                  rating={Math.round(
                                    reviewStats.averageRating
                                  )}
                                  size={14}
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => setShowReviewsPreview(!showReviewsPreview)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                              showReviewsPreview
                                ? "border-amber-300 bg-amber-50 text-amber-700"
                                : "border-slate-200 text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            <Eye className="w-4 h-4" />
                            {showReviewsPreview ? "Hide Preview" : "Preview"}
                          </button>
                          <button
                            onClick={() =>
                              setShowReviewsEmbed(!showReviewsEmbed)
                            }
                            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
                            style={{
                              backgroundColor:
                                "var(--color-primary, #0F5132)",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "var(--color-primary-hover, #146C43)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "var(--color-primary, #0F5132)")
                            }
                          >
                            <Code className="w-4 h-4" />
                            Get Embed Code
                          </button>
                          <button
                            onClick={() =>
                              setShowReviewsTable(!showReviewsTable)
                            }
                            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Star className="w-4 h-4" />
                            {showReviewsTable
                              ? "Hide Reviews"
                              : "Manage Reviews"}
                          </button>
                        </div>

                        {/* Inline Reviews Preview */}
                        <AnimatePresence>
                          {showReviewsPreview && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <WidgetPreview
                                type="reviews"
                                color={embedColor}
                                firmSlug={firmSlug}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Google Business Connection */}
                        {gbLoading ? (
                          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex items-center gap-3">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-600" />
                            <span className="text-sm text-slate-500">Loading Google Business status...</span>
                          </div>
                        ) : gbStatus.connected ? (
                          /* ── Connected state: status bar (matches CalendarIntegrationBanner connected style) ── */
                          <div
                            className="rounded-xl border border-slate-200 border-l-4 p-4 bg-white"
                            style={{ borderLeftColor: "var(--color-accent, #198754)" }}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                              <div className="flex items-center gap-3 flex-1">
                                <div className="h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-accent)" }}>
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
                                  <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                  </svg>
                                  <span className="text-sm font-medium text-slate-700">Google Business</span>
                                  <span className="text-xs text-slate-500">
                                    {gbStatus.placeName || gbStatus.googleAccountEmail || "Connected"}
                                  </span>
                                  {/* Sync button */}
                                  <button
                                    onClick={handleSyncGoogle}
                                    disabled={gbSyncing}
                                    className="p-1 rounded-lg hover:bg-slate-200 transition-colors text-slate-400 hover:text-slate-600 disabled:opacity-50"
                                    title="Sync Now"
                                  >
                                    <RefreshCw className={`h-3.5 w-3.5 ${gbSyncing ? "animate-spin" : ""}`} />
                                  </button>
                                  {/* Disconnect button */}
                                  <button
                                    onClick={handleDisconnectGoogle}
                                    className="p-1 rounded-lg hover:bg-red-100 transition-colors text-slate-400 hover:text-red-600"
                                    title="Disconnect"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                {gbSyncing && (
                                  <span className="text-xs text-slate-500 animate-pulse">Syncing...</span>
                                )}
                              </div>
                            </div>
                            {gbStatus.lastSyncAt && (
                              <div className="mt-2 text-xs text-slate-400">
                                Last synced: {new Date(gbStatus.lastSyncAt).toLocaleString([], {
                                  month: "short",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          /* ── Disconnected state: connect banner (matches CalendarIntegrationBanner style) ── */
                          <div className="bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-200 p-5">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                              <div className="flex items-start gap-4 flex-1">
                                <div
                                  className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: "rgba(15, 81, 50, 0.1)" }}
                                >
                                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-base font-bold text-slate-800">
                                    Connect Google Business
                                  </h3>
                                  <p className="text-sm text-slate-500 mt-1">
                                    Sync your Google Business reviews automatically. Reviews will appear alongside your widget reviews.
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={handleConnectGoogle}
                                disabled={gbConnecting}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200 flex-shrink-0 disabled:opacity-50"
                              >
                                {gbConnecting ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                  </svg>
                                )}
                                {gbConnecting ? "Connecting..." : "Connect Google Business"}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Embed Code Panel */}
                        <AnimatePresence>
                          {showReviewsEmbed && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden space-y-4"
                            >
                              {/* Color Customization */}
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Palette className="w-4 h-4 text-slate-500" />
                                  <h4 className="text-sm font-medium text-slate-700">Widget Color</h4>
                                </div>
                                <div className="flex items-center gap-3">
                                  <input
                                    type="color"
                                    value={`#${embedColor}`}
                                    onChange={(e) => setEmbedColor(e.target.value.replace("#", ""))}
                                    className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5"
                                  />
                                  <div className="relative flex-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">#</span>
                                    <input
                                      type="text"
                                      value={embedColor}
                                      onChange={(e) => setEmbedColor(e.target.value.replace("#", "").replace(/[^a-fA-F0-9]/g, "").slice(0, 6))}
                                      className="w-full rounded-xl border border-slate-200 pl-7 pr-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                                      placeholder="0F5132"
                                      maxLength={6}
                                    />
                                  </div>
                                </div>
                              </div>

                              <EmbedCodeDisplay
                                code={getReviewsEmbedCode()}
                                instructions="Copy the code below and paste it into your website's HTML where you want the reviews widget to appear."
                              />

                              <WidgetPreview
                                type="reviews"
                                color={embedColor}
                                firmSlug={firmSlug}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Reviews Table */}
                        <AnimatePresence>
                          {showReviewsTable && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              {reviews.length === 0 ? (
                                <EmptyState
                                  icon={Star}
                                  title="No reviews yet"
                                  message={gbStatus.connected
                                    ? "Click 'Sync Now' on your Google Business connection to pull in reviews, or embed the widget on your website to collect new ones."
                                    : "Connect your Google Business Profile above to import existing reviews, or embed the widget on your website to start collecting new ones."
                                  }
                                />
                              ) : (
                                <div className="border border-slate-200 rounded-xl overflow-hidden">
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                      <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                          <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                            Reviewer
                                          </th>
                                          <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                            Rating
                                          </th>
                                          <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                            Review
                                          </th>
                                          <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                            Status
                                          </th>
                                          <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right">
                                            Actions
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {reviews.map((review) => (
                                          <tr
                                            key={review._id}
                                            className="hover:bg-slate-50 transition-colors"
                                          >
                                            <td className="px-4 py-3">
                                              <div className="flex items-center gap-1.5">
                                                <p className="text-sm font-medium text-slate-900">
                                                  {review.reviewerName}
                                                </p>
                                                {review.source === "google" && (
                                                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 flex-shrink-0" fill="none">
                                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                                  </svg>
                                                )}
                                              </div>
                                              <p className="text-xs text-slate-500 mt-0.5">
                                                {new Date(
                                                  review.createdAt
                                                ).toLocaleDateString()}
                                              </p>
                                            </td>
                                            <td className="px-4 py-3">
                                              <StarRating
                                                rating={review.rating}
                                                size={14}
                                              />
                                            </td>
                                            <td className="px-4 py-3 max-w-xs">
                                              <p className="text-sm text-slate-600 truncate">
                                                {review.text}
                                              </p>
                                            </td>
                                            <td className="px-4 py-3">
                                              <StatusBadge
                                                status={review.status}
                                              />
                                            </td>
                                            <td className="px-4 py-3">
                                              <div className="flex items-center justify-end gap-1">
                                                {review.status ===
                                                  "pending" && (
                                                  <>
                                                    <button
                                                      onClick={() =>
                                                        handleReviewAction(
                                                          review._id,
                                                          "approve"
                                                        )
                                                      }
                                                      disabled={
                                                        actioningReviewId ===
                                                        review._id
                                                      }
                                                      className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors disabled:opacity-50"
                                                      title="Approve"
                                                    >
                                                      <ThumbsUp className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                      onClick={() =>
                                                        handleReviewAction(
                                                          review._id,
                                                          "reject"
                                                        )
                                                      }
                                                      disabled={
                                                        actioningReviewId ===
                                                        review._id
                                                      }
                                                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors disabled:opacity-50"
                                                      title="Reject"
                                                    >
                                                      <ThumbsDown className="w-4 h-4" />
                                                    </button>
                                                  </>
                                                )}
                                                {review.status ===
                                                  "rejected" && (
                                                  <button
                                                    onClick={() =>
                                                      handleReviewAction(
                                                        review._id,
                                                        "approve"
                                                      )
                                                    }
                                                    disabled={
                                                      actioningReviewId ===
                                                      review._id
                                                    }
                                                    className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors disabled:opacity-50"
                                                    title="Approve"
                                                  >
                                                    <ThumbsUp className="w-4 h-4" />
                                                  </button>
                                                )}
                                                {review.status ===
                                                  "approved" && (
                                                  <button
                                                    onClick={() =>
                                                      handleReviewAction(
                                                        review._id,
                                                        "reject"
                                                      )
                                                    }
                                                    disabled={
                                                      actioningReviewId ===
                                                      review._id
                                                    }
                                                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors disabled:opacity-50"
                                                    title="Reject"
                                                  >
                                                    <ThumbsDown className="w-4 h-4" />
                                                  </button>
                                                )}
                                                <button
                                                  onClick={() =>
                                                    handleReviewAction(
                                                      review._id,
                                                      "delete"
                                                    )
                                                  }
                                                  disabled={
                                                    actioningReviewId ===
                                                    review._id
                                                  }
                                                  className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                                  title="Delete"
                                                >
                                                  <Trash2 className="w-4 h-4" />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          Booking Embed Code Modal
          ═══════════════════════════════════════════════════════════════════════ */}
      <PremiumModal
        open={!!embedModalBooking}
        onClose={() => setEmbedModalBooking(null)}
        title="Booking Widget Embed Code"
        subtitle={embedModalBooking?.name}
        icon={Calendar}
        size="lg"
      >
        {embedModalBooking && (
          <div className="space-y-6">
            {/* Color Customization */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Palette className="w-4 h-4 text-slate-500" />
                <h4 className="text-sm font-medium text-slate-700">Widget Color</h4>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={`#${embedColor}`}
                  onChange={(e) => setEmbedColor(e.target.value.replace("#", ""))}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5"
                />
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">#</span>
                  <input
                    type="text"
                    value={embedColor}
                    onChange={(e) => setEmbedColor(e.target.value.replace("#", "").replace(/[^a-fA-F0-9]/g, "").slice(0, 6))}
                    className="w-full rounded-xl border border-slate-200 pl-7 pr-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                    placeholder="0F5132"
                    maxLength={6}
                  />
                </div>
              </div>
            </div>

            <EmbedCodeDisplay
              code={getBookingEmbedCode(embedModalBooking)}
              directLink={getBookingDirectLink(embedModalBooking)}
              instructions="Copy the code below and paste it into your website's HTML where you want the booking widget to appear."
            />

            <WidgetPreview
              type="booking"
              slug={embedModalBooking.slug}
              color={embedColor}
              firmSlug={firmSlug}
            />
          </div>
        )}
      </PremiumModal>

      {/* ═══════════════════════════════════════════════════════════════════════
          Form Embed Code Modal
          ═══════════════════════════════════════════════════════════════════════ */}
      <PremiumModal
        open={!!embedModalForm}
        onClose={() => setEmbedModalForm(null)}
        title="Contact Form Widget Embed Code"
        subtitle={embedModalForm?.name}
        icon={FileText}
        size="lg"
      >
        {embedModalForm && (
          <div className="space-y-6">
            {/* Color Customization */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Palette className="w-4 h-4 text-slate-500" />
                <h4 className="text-sm font-medium text-slate-700">Widget Color</h4>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={`#${embedColor}`}
                  onChange={(e) => setEmbedColor(e.target.value.replace("#", ""))}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5"
                />
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">#</span>
                  <input
                    type="text"
                    value={embedColor}
                    onChange={(e) => setEmbedColor(e.target.value.replace("#", "").replace(/[^a-fA-F0-9]/g, "").slice(0, 6))}
                    className="w-full rounded-xl border border-slate-200 pl-7 pr-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                    placeholder="0F5132"
                    maxLength={6}
                  />
                </div>
              </div>
            </div>

            <EmbedCodeDisplay
              code={getFormEmbedCode(embedModalForm)}
              instructions="Copy the code below and paste it into your website's HTML where you want the contact form to appear."
            />

            <WidgetPreview
              type="form"
              slug={embedModalForm.slug || embedModalForm._id}
              color={embedColor}
              firmSlug={firmSlug}
            />
          </div>
        )}
      </PremiumModal>

      {/* Booking Type Modal (full wizard from calendar) */}
      <BookingTypeModal
        open={showCreateBooking}
        onClose={() => setShowCreateBooking(false)}
        onSave={() => { fetchBookingTypes(); }}
      />
      <QuickCreateIntakeFormModal
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onCreated={fetchIntakeForms}
      />
    </div>
  );
};

export default WidgetsPage;
